const React = require('react');
const ControlledTextareaField = require('./ControlledTextareaField');
const ClearFieldAdornment = require('./ClearFieldAdornment');
const FieldContext = require('./FieldContext');

const internals = {};

internals.mergeFieldStyles = (...styles) => styles
    .filter(Boolean)
    .join('\n');

internals.hasClearValue = (value) => value !== null
    && typeof value !== 'undefined'
    && String(value).length > 0;

internals.normalizeRequiredLabel = ({
    label,
    required
}) => {

    if (!required || typeof label !== 'string') {
        return label;
    }

    return label.replace(/\s*\*+\s*$/, '');
};

module.exports = function FormTextareaField(props) {
    const {
        field = {},
        fieldName = field.name,
        formProps = {},
        label = field.label,
        onBlur,
        onChange,
        onFocus,
        setValue,
        textFieldProps = {},
        useField: requestedUseFieldHook,
        value
    } = props;

    const resolvedUseField = typeof requestedUseFieldHook === 'function'
        ? requestedUseFieldHook
        : FieldContext.useField;

    const fieldController = resolvedUseField(fieldName);
    const resolvedValue = typeof value === 'undefined'
        ? fieldController.getValue()
        : value;
    const resolvedTextFieldProps = fieldController.getTextFieldProps?.(textFieldProps) || textFieldProps;
    const hasError = !!resolvedTextFieldProps.error;
    const helperText = resolvedTextFieldProps.helperText;
    const canClear = !!field.clearable;
    const hasClearValue = internals.hasClearValue(resolvedValue);
    const clearLabel = field.clearLabel || `Clear ${label || fieldName}`;
    const required = !!(field.required || resolvedTextFieldProps.required);
    const placeholder = typeof field.placeholder === 'function'
        ? field.placeholder({ formProps })
        : field.placeholder;
    const labelPlaceholder = field.placeholderInLabel && placeholder && !hasClearValue
        ? `${label}: ${placeholder}`
        : null;

    const setTextareaValue = (nextValue, event) => {

        if (typeof setValue === 'function') {
            setValue(nextValue, event);
            return;
        }

        fieldController.setValue(nextValue, undefined, event);
    };

    const handleChange = (nextValue, event) => {

        setTextareaValue(nextValue, event);

        if (typeof onChange === 'function') {
            onChange(nextValue, event);
        }
    };

    return <ControlledTextareaField
            label={internals.normalizeRequiredLabel({
                label: labelPlaceholder || label,
                required
            })}
            value={resolvedValue}
            onBlur={(event) => {
                fieldController.onBlur?.(event);
                onBlur?.(event);
            }}
            onChange={handleChange}
            onFocus={(event) => {
                fieldController.onFocus?.(event);
                onFocus?.(event);
            }}
            textFieldProps={{
                ...resolvedTextFieldProps,
                placeholder: field.placeholderInLabel
                    ? undefined
                    : resolvedTextFieldProps.placeholder || placeholder,
                required,
                error: !!hasError,
                helperText,
                InputLabelProps: {
                    ...(resolvedTextFieldProps.InputLabelProps || {}),
                    ...(!field.placeholderInLabel && placeholder ? { shrink: true } : {}),
                    ...(labelPlaceholder ? { shrink: false } : {})
                },
                InputProps: {
                    ...(resolvedTextFieldProps.InputProps || {}),
                    endAdornment: canClear ? <ClearFieldAdornment
                        clearLabel={clearLabel}
                        disabled={resolvedTextFieldProps.disabled || !hasClearValue}
                        onClear={(event) => {
                            if (!hasClearValue) {
                                return;
                            }

                            handleChange('', event);
                        }}
                        existingEndAdornment={resolvedTextFieldProps.InputProps?.endAdornment}
                    /> : resolvedTextFieldProps.InputProps?.endAdornment
                },
                className: [
                    resolvedTextFieldProps.className,
                    labelPlaceholder ? 'np-placeholder-in-label' : null
                ].filter(Boolean).join(' ') || undefined,
                fieldStyles: resolvedTextFieldProps.fieldStyles,
                inputProps: {
                    ...(resolvedTextFieldProps.inputProps || {}),
                    'aria-invalid': hasError || undefined
                }
            }}
        />;
};

module.exports.internals = internals;
